"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _react = _interopRequireWildcard(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _utils = require("./utils");
function _getRequireWildcardCache(e) { if ("function" != typeof WeakMap) return null; var r = new WeakMap(), t = new WeakMap(); return (_getRequireWildcardCache = function (e) { return e ? t : r; })(e); }
function _interopRequireWildcard(e, r) { if (!r && e && e.__esModule) return e; if (null === e || "object" != typeof e && "function" != typeof e) return { default: e }; var t = _getRequireWildcardCache(r); if (t && t.has(e)) return t.get(e); var n = { __proto__: null }, a = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var u in e) if ("default" !== u && Object.prototype.hasOwnProperty.call(e, u)) { var i = a ? Object.getOwnPropertyDescriptor(e, u) : null; i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u]; } return n.default = e, t && t.set(e, n), n; }
/**
 * Returns step value associated to position.
 *
 * @param {number} pos Position to find value of
 * @param {number} min react props
 * @param {number} renderWidth slider client width
 * @param {React.Ref} slider slider reference
 * @returns {number} Step value of specified position
 */
function getValueFromPosition(pos, _ref, renderWidth, slider) {
  let {
    min,
    max,
    rtl,
    step
  } = _ref;
  const boundingRect = slider.current.getBoundingClientRect();
  const left = boundingRect.left;
  pos -= left + _utils.SLIDER_BAR_HEIGHT / 2;
  if (rtl) pos = renderWidth - pos;
  const percentage = pos / renderWidth;
  return (0, _utils.getNearestStepValue)(min + percentage * (max - min), min, max, step);
}

/**
 * Clears value label auto hide timer for specified thumb.
 *
 * @param {function} autoHide Auto hide values
 * @param {number} index Index of thumb
 * @private
 */
function stopAutoHideTimer(autoHide, index) {
  if (autoHide[index]) {
    window.clearTimeout(autoHide[index]);
  }
}

/**
 * Starts value label auto hide timer for specified thumb.
 *
 * @param {number} props.index Index of thumb
 * @param {array} props.autoHideTimerHandle memoized timeout values
 * @param {array} props.valueLabels displayed values
 * @param {function} props.setValueLabels updates valueLabels
 * @param {number} props.autoHideValue auto hide value labels time
 * @private
 */
function startAutoHideTimer(_ref2) {
  let {
    index,
    autoHideTimerHandle: autoHide,
    setValueLabels,
    autoHideValue
  } = _ref2;
  stopAutoHideTimer(autoHide, index);
  autoHide[index] = window.setTimeout(hideValueLabel.bind(null, index, setValueLabels), autoHideValue);
}

/**
 * Sets valueLabels state to false for specified index.
 *
 * @param {number} index Index of thumb
 * @param {function} setValueLabels updates valueLabel position
 * @private
 */
function hideValueLabel(index, setValueLabels) {
  setValueLabels(prevValueLabels => {
    const next = [...prevValueLabels];
    next[index] = false;
    return next;
  });
}

/**
 * Sets valueLabels state to true for specified index.
 *
 * @param {number} index Index of thumb
 * @param {function} setValueLabels updates valueLabel position
 */
function showValueLabel(index, setValueLabels) {
  setValueLabels(prevValueLabels => {
    const next = [...prevValueLabels];
    next[index] = true;
    return next;
  });
}

/**
  * Slider Thumb component
  *
  * @param {React.PropsWithoutRef} props components props
  * @returns {React.FunctionComponent} React slider thumb
  */
function SliderThumb(_ref3) {
  let {
    index,
    id,
    max,
    min,
    value,
    dir,
    renderWidth,
    shouldRenderLabels,
    range,
    step,
    minRangeStep,
    rtl,
    setValue,
    setValueLabels,
    sliderRef,
    allowClickUpdate,
    autoHide,
    ariaLabels,
    tabIndex,
    currentValues
  } = _ref3;
  const autoHideTimerHandle = (0, _react.useCallback)(() => range ? [null, null] : [null], [range]);

  /**
   * @typedef {object} EventWithXPosition
   * @prop {number} clientX horizontal position of event location
   */
  /**
   * Updates value state.
   *
   * @param {EventWithXPosition} e Mouse or Touch Event
   * @private
   */
  const updateValue = (0, _utils.useEventCallback)(e => {
    if (!sliderRef.current) return;
    let thumbVal = getValueFromPosition(e.clientX, {
      min,
      max,
      rtl,
      step
    }, renderWidth, sliderRef);
    let newVal;
    if (range) {
      if (index === 0) {
        thumbVal = Math.min(thumbVal, currentValues[1] - step * minRangeStep);
        newVal = [thumbVal, currentValues[1]];
      } else {
        thumbVal = Math.max(thumbVal, currentValues[0] + step * minRangeStep);
        newVal = [currentValues[0], thumbVal];
      }
    } else {
      newVal = thumbVal;
    }
    setValue(newVal);
  });

  /**
   * Displays value labels on mouse enter.
   *
   * @param {number} index Index of thumb
   * @private
   */
  const handleThumbMouseEnter = (0, _utils.useEventCallback)(() => {
    if (!shouldRenderLabels) {
      showValueLabel(index, setValueLabels);
      stopAutoHideTimer(index);
    }
  });

  /**
   * Starts value label auto hide timer on mouse leave.
   *
   * @param {number} index Index of thumb
   * @private
   */
  const handleThumbMouseLeave = (0, _utils.useEventCallback)(() => {
    if (!shouldRenderLabels) {
      startAutoHideTimer({
        index,
        autoHideTimerHandle,
        setValueLabels,
        autoHide
      });
    }
  });

  /**
   * Decrease the value of the slider based on the thumb index.
   *
   * @param {React.KeyboardEvent} ev key event
   * @private
   */
  const decreaseRangeThumbValue = (0, _utils.useEventCallback)(ev => {
    ev.preventDefault();
    if (!allowClickUpdate || min >= value) return;
    let newVal;
    if (range) {
      const [prevLeft, prevRight] = currentValues;
      if (index === 0) {
        newVal = Math.max(min, prevLeft - step);
      } else {
        newVal = Math.max(currentValues[index] - step, prevLeft + minRangeStep * step);
      }
      newVal = index === 0 ? [newVal, prevRight] : [prevLeft, newVal];
    } else {
      newVal = Math.max(min, value - step);
    }
    setValue(newVal);
  });

  /**
   * Increase the value of the slider based on the thumb index.
   *
   * @param {React.KeyboardEvent} ev key event
   * @private
   */
  const increaseRangeThumbValue = (0, _utils.useEventCallback)(ev => {
    ev.preventDefault();
    if (!allowClickUpdate || max <= value) return;
    let newVal;
    if (range) {
      const [prevLeft, prevRight] = currentValues;
      if (index === 1) {
        newVal = Math.min(max, prevRight + step);
      } else {
        newVal = Math.min(currentValues[index] + step, prevRight - minRangeStep * step);
      }
      newVal = index === 0 ? [newVal, prevRight] : [prevLeft, newVal];
    } else {
      newVal = Math.min(max, value + step);
    }
    setValue(newVal);
  });

  /**
   * Updates value on touch event.
   *
   * @param {React.TouchEvent} e Touch Event
   * @private
   */
  const handleTouch = (0, _utils.useEventCallback)(e => {
    const touch = e.changedTouches[0];
    updateValue(touch);
  });

  /**
   * Updates value and triggers mouse enter while dragging thumb.
   *
   * @param {React.DragEvent} e Touch Event
   * @private
   */
  const handleDrag = (0, _utils.useEventCallback)(e => {
    showValueLabel(index, setValueLabels);
    updateValue(e);
  });

  /**
   * Removes event listeners and triggers mouse leave when thumb is released.
   *
   * @param {number} index Index of released thumb
   * @private
   */
  const handleMouseUp = (0, _utils.useEventCallback)(() => {
    // eslint-disable-next-line no-use-before-define
    removeEventListeners();
  });

  /**
   * Removes event listeners added during mouse down.
   *
   * @private
   */
  const removeEventListeners = (0, _react.useCallback)(() => {
    window.document.removeEventListener('mousemove', handleDrag);
    window.document.removeEventListener('mouseup', handleMouseUp);
  }, [handleDrag, handleMouseUp]);
  (0, _react.useEffect)(() => {
    return () => {
      for (let i = 0; i < autoHideTimerHandle.length; i++) {
        if (autoHideTimerHandle[i]) {
          window.clearTimeout(autoHideTimerHandle[i]);
        }
      }
      removeEventListeners();
    };
  }, [autoHideTimerHandle, removeEventListeners]);

  /**
   * Adds listeners and triggers mouse enter on mouse down.
   *
   * @param {React.MouseEvent} e Mouse Event
   * @param {number} index Index of dragged thumb
   * @private
   */
  const handleThumbMouseDown = (0, _utils.useEventCallback)(e => {
    e.stopPropagation();
    e.preventDefault();
    window.document.addEventListener('mousemove', handleDrag);
    window.document.addEventListener('mouseup', handleMouseUp);
  });

  /**
   * Handle a key press event on a thumb
   *
   * @param {React.KeyboardEvent} evt Event object for the key press
   */
  const handleThumbKeyPress = (0, _utils.useEventCallback)(evt => {
    switch (evt.key) {
      case 'ArrowRight':
        rtl ? decreaseRangeThumbValue(evt) : increaseRangeThumbValue(evt);
        break;
      case 'ArrowLeft':
        rtl ? increaseRangeThumbValue(evt) : decreaseRangeThumbValue(evt);
        break;
      case 'ArrowUp':
        increaseRangeThumbValue(evt);
        break;
      case 'ArrowDown':
        decreaseRangeThumbValue(evt);
        break;
      default:
        return;
    }
  });
  return /*#__PURE__*/_react.default.createElement("div", {
    id: id + '-thumb-' + index,
    className: "ux-slider-thumb",
    role: "slider",
    tabIndex: tabIndex,
    "aria-label": ariaLabels,
    "aria-valuemin": min,
    "aria-valuemax": max,
    "aria-valuenow": value,
    style: {
      [dir]: (0, _utils.getPositionFromValue)(value, min, max, renderWidth)
    },
    onMouseDown: handleThumbMouseDown,
    onTouchStart: handleTouch,
    onTouchMove: handleTouch,
    onMouseEnter: handleThumbMouseEnter,
    onMouseLeave: handleThumbMouseLeave,
    onKeyDown: handleThumbKeyPress,
    onFocus: handleThumbMouseEnter,
    onBlur: handleThumbMouseLeave
  });
}
SliderThumb.propTypes = {
  index: _propTypes.default.number,
  id: _propTypes.default.string,
  max: _propTypes.default.number,
  min: _propTypes.default.number,
  value: _propTypes.default.oneOfType([_propTypes.default.number, _propTypes.default.array]),
  dir: _propTypes.default.string,
  renderWidth: _propTypes.default.number,
  sliderRef: _propTypes.default.oneOfType([_propTypes.default.func, _propTypes.default.shape({
    current: _propTypes.default.any
  })]),
  autoHide: _propTypes.default.number,
  shouldRenderLabels: _propTypes.default.bool,
  range: _propTypes.default.bool,
  step: _propTypes.default.number,
  minRangeStep: _propTypes.default.number,
  rtl: _propTypes.default.bool,
  setValue: _propTypes.default.func,
  valueLabels: _propTypes.default.array,
  setValueLabels: _propTypes.default.func,
  allowClickUpdate: _propTypes.default.bool,
  /** Developer indicates tabIndex, default is 0 */
  tabIndex: _propTypes.default.oneOf([-1, 0]),
  /** Screenreader labels for sub-elements */
  ariaLabels: _propTypes.default.string,
  currentValues: _propTypes.default.oneOfType([_propTypes.default.number, _propTypes.default.array])
};
SliderThumb.defaultProps = {
  tabIndex: 0,
  ariaLabels: 'Slider range handle'
};
var _default = exports.default = SliderThumb;